import { 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  where,
  onSnapshot, 
  doc, 
  updateDoc,
  arrayUnion,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

// Create a notification when someone is mentioned
export const createMentionNotification = async (mentionedUserEmail, mentionedByUser, contentType, contentId, channelId, postId = null) => {
  try {
    const docRef = await addDoc(collection(db, 'notifications'), {
      type: 'mention',
      mentionedUserEmail: mentionedUserEmail,
      mentionedByUserId: mentionedByUser.uid,
      mentionedByUserEmail: mentionedByUser.email,
      contentType: contentType, // 'post' or 'reply'
      contentId: contentId, // post ID or reply ID
      postId: postId, // for replies, this is the parent post ID
      channelId: channelId,
      createdAt: serverTimestamp(),
      seenBy: [], // Array of user IDs who have seen this notification
      isRead: false
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating mention notification:', error);
    throw error;
  }
};

// Get notifications for a specific user (unseen only by default)
export const subscribeToUserNotifications = (userEmail, callback, includeRead = false) => {
  let q = query(
    collection(db, 'notifications'),
    where('mentionedUserEmail', '==', userEmail),
    orderBy('createdAt', 'desc')
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const notifications = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Filter out read notifications unless explicitly requested
      if (includeRead || !data.seenBy.includes(userEmail)) {
        notifications.push({
          id: doc.id,
          ...data
        });
      }
    });
    callback(notifications);
  });
};

// Mark notification as seen/read
export const markNotificationAsSeen = async (notificationId, userEmail) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      seenBy: arrayUnion(userEmail),
      isRead: true
    });
    return true;
  } catch (error) {
    console.error('Error marking notification as seen:', error);
    throw error;
  }
};

// Mark all notifications as seen for a user
export const markAllNotificationsAsSeen = async (userEmail) => {
  try {
    // This is a simplified approach - in production, you might want to use batch operations
    const q = query(
      collection(db, 'notifications'),
      where('mentionedUserEmail', '==', userEmail),
      where('isRead', '==', false)
    );
    
    return new Promise((resolve, reject) => {
      const unsubscribe = onSnapshot(q, async (querySnapshot) => {
        try {
          const updatePromises = [];
          querySnapshot.forEach((doc) => {
            const notificationRef = doc.ref;
            updatePromises.push(
              updateDoc(notificationRef, {
                seenBy: arrayUnion(userEmail),
                isRead: true
              })
            );
          });
          
          await Promise.all(updatePromises);
          unsubscribe();
          resolve();
        } catch (error) {
          unsubscribe();
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error('Error marking all notifications as seen:', error);
    throw error;
  }
};

// Get notification count for a user (unread only)
export const getUnreadNotificationCount = (userEmail, callback) => {
  const q = query(
    collection(db, 'notifications'),
    where('mentionedUserEmail', '==', userEmail),
    where('isRead', '==', false)
  );
  
  return onSnapshot(q, (querySnapshot) => {
    callback(querySnapshot.size);
  });
};

// Delete old notifications (cleanup function)
export const deleteOldNotifications = async (daysOld = 30) => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);
    
    // This would typically be done in a Cloud Function for better performance
    // For now, we'll keep it simple
    console.log(`Cleanup function: would delete notifications older than ${daysOld} days`);
  } catch (error) {
    console.error('Error deleting old notifications:', error);
    throw error;
  }
};